<template>
  <!--
    Per-card lightbox for the Custom Deck builder's full-deck grid.
    Stays open during regen — shows a loading overlay over the image
    until the new render lands (parent watches for image_url change
    on the card prop and updates accordingly). Includes a card-version
    archive (mirrors MCP) so operator can Make Live an older render.
  -->
  <div class="lightbox-backdrop" @click.self="$emit('close')">
    <button class="close-btn" @click="$emit('close')" aria-label="Close">×</button>

    <div class="lightbox-content">
      <div class="image-pane">
        <div class="image-frame" :class="{ refreshing }">
          <img
            v-if="card?.image_url"
            :src="card.image_url"
            :alt="card.card_name"
            class="big-img"
          />
          <div v-else class="missing">No image</div>
          <div v-if="refreshing" class="refresh-overlay">
            <span class="overlay-spinner"></span>
            <span class="overlay-text">Generating new image…</span>
            <span class="overlay-elapsed">
              {{ elapsedDisplay }} · ~90s typical
            </span>
            <span v-if="elapsed >= 75" class="overlay-extra">
              Complex compositions can take longer - hang tight.
            </span>
          </div>
        </div>
        <div class="caption">{{ card?.card_name || '' }}</div>
      </div>

      <div class="edit-pane">
        <label class="field-label">
          <span>Generation prompt</span>
          <small>What the image model uses for this card. Editing here updates the deck-wide prompt; only this card re-renders right now.</small>
        </label>
        <textarea
          v-model="localPrompt"
          rows="6"
          :disabled="!editable || refreshing"
          :placeholder="editable ? 'Describe rendering style, palette, lighting, recurring motifs.' : ''"
        ></textarea>

        <div v-if="!editable" class="locked-note">
          🔒 Editing locked while the deck finishes audit. Available
          once all 78 cards are complete.
        </div>

        <div class="actions">
          <button
            class="primary-btn"
            :disabled="!editable || refreshing || !localPrompt?.trim()"
            @click="onRefresh"
          >{{ refreshing ? 'Generating…' : 'Regenerate this card (50 carats)' }}</button>
        </div>

        <p v-if="error" class="error">⚠ {{ error }}</p>

        <!-- Version archive — mirrors MCP. Always expanded since
             we have room. Newest first. Make Live to promote a
             previous render back. -->
        <div class="archive-section">
          <header class="archive-head">
            <span class="archive-label">Archive</span>
            <span class="archive-count">
              {{ versions.archive.length }} previous version{{ versions.archive.length === 1 ? '' : 's' }}
            </span>
          </header>

          <div v-if="loadingVersions" class="archive-status">Loading…</div>

          <div v-else-if="!versions.archive.length" class="archive-empty">
            No previous versions yet. Each regenerate snapshots the
            current image here before overwriting.
          </div>

          <div v-else class="archive-strip">
            <div
              v-for="v in versions.archive"
              :key="v.version_number"
              class="archive-tile"
            >
              <div class="archive-frame">
                <img
                  v-if="v.image_url"
                  :src="v.image_url"
                  :alt="`v${v.version_number}`"
                />
              </div>
              <div class="archive-meta">
                <div class="archive-version">v{{ v.version_number }}</div>
                <div class="archive-date">{{ formatDate(v.archived_at) }}</div>
              </div>
              <button
                class="make-live-btn"
                :disabled="makingLive === v.version_number || !editable"
                @click="onMakeLive(v.version_number)"
              >{{ makingLive === v.version_number ? 'Promoting…' : 'Make Live' }}</button>
            </div>
          </div>
          <p v-if="versionsError" class="error">⚠ {{ versionsError }}</p>
        </div>

        <!-- ── Card Meaning — the symbolic read that drives the reveal ── -->
        <div v-if="!card?.is_back" class="meaning-section">
          <header class="meaning-head">
            <span class="meaning-label">Card Meaning</span>
            <span class="meaning-note">What the reveal interprets from · view only for now</span>
          </header>

          <div v-if="loadingMeaning" class="archive-status">Loading…</div>
          <template v-else-if="meaning">
            <div class="meaning-block">
              <div class="meaning-source-label">✦ Symbolic <small>(this reader)</small></div>
              <p v-if="!meaning.has_symbolic" class="meaning-empty">
                No symbolic meaning authored yet - reveals fall back to the generic reading below.
              </p>
              <template v-else>
                <p v-if="meaning.symbolic.reader_card_name" class="meaning-name">{{ meaning.symbolic.reader_card_name }}</p>
                <p v-if="meaning.symbolic.description" class="meaning-text">{{ meaning.symbolic.description }}</p>
                <p v-if="meaning.symbolic.reverse_description" class="meaning-text meaning-reversed"><b>Reversed:</b> {{ meaning.symbolic.reverse_description }}</p>
                <p v-if="meaning.symbolic.symbols_description" class="meaning-text meaning-dim">{{ meaning.symbolic.symbols_description }}</p>
                <div class="meaning-chips">
                  <span v-if="meaning.symbolic.archetype_meaning" class="meaning-chip" :title="meaning.symbolic.archetype_meaning">Archetype</span>
                  <span v-if="meaning.symbolic.number_meaning" class="meaning-chip" :title="meaning.symbolic.number_meaning">Number</span>
                  <span v-if="meaning.symbolic.suit_meaning" class="meaning-chip" :title="meaning.symbolic.suit_meaning">Suit</span>
                  <span v-if="meaning.symbolic.element_meaning" class="meaning-chip" :title="meaning.symbolic.element_meaning">Element</span>
                </div>
              </template>
            </div>

            <div class="meaning-block meaning-generic">
              <div class="meaning-source-label">Generic <small>(canonical RWS)</small></div>
              <p v-if="meaning.generic.description" class="meaning-text meaning-dim">{{ meaning.generic.description }}</p>
              <p v-if="meaning.generic.reverse_description" class="meaning-text meaning-dim meaning-reversed"><b>Reversed:</b> {{ meaning.generic.reverse_description }}</p>
            </div>
          </template>
          <p v-if="meaningError" class="error">⚠ {{ meaningError }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import * as api from '@/api-service.js'

const props = defineProps({
  card: { type: Object, default: null },
  deckSlug: { type: String, default: '' },
  deckPrompt: { type: String, default: '' },
  editable: { type: Boolean, default: false },
  refreshing: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
const emit = defineEmits(['close', 'refresh', 'made-live'])

const localPrompt = ref(props.deckPrompt || '')

// ── Elapsed-seconds counter for the regen overlay ──────────────
// Starts when ``refreshing`` flips true, clears when it flips back.
// Operator can see honest progress instead of an opaque spinner.
const elapsed = ref(0)
let elapsedTimer = null
function startElapsed() {
  elapsed.value = 0
  if (elapsedTimer) clearInterval(elapsedTimer)
  elapsedTimer = setInterval(() => { elapsed.value += 1 }, 1000)
}
function stopElapsed() {
  if (elapsedTimer) clearInterval(elapsedTimer)
  elapsedTimer = null
  elapsed.value = 0
}
watch(
  () => props.refreshing,
  (now) => { if (now) startElapsed(); else stopElapsed() },
  { immediate: true },
)
const elapsedDisplay = computed(() => {
  const s = elapsed.value
  const m = Math.floor(s / 60)
  const r = s % 60
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${s}s`
})
watch(
  () => [props.card?.card_id, props.deckPrompt],
  () => { localPrompt.value = props.deckPrompt || '' },
  { immediate: false },
)

// ── Version archive ─────────────────────────────────────────────
const versions = ref({ live: null, archive: [] })
const loadingVersions = ref(false)
const versionsError = ref('')
const makingLive = ref(null)

async function loadVersions() {
  if (!props.deckSlug) return
  const isBack = !!props.card?.is_back
  if (!isBack && !props.card?.card_id) return
  loadingVersions.value = true
  versionsError.value = ''
  try {
    versions.value = isBack
      ? await api.getCustomDeckBackVersions(props.deckSlug)
      : await api.getCustomDeckCardVersions(
          props.deckSlug, props.card.card_id,
        )
  } catch (e) {
    versionsError.value = e?.response?.data?.error || String(e)
  } finally {
    loadingVersions.value = false
  }
}

// Refresh the archive when the card changes OR when a regen finishes
// (refreshing flips false → the BE just snapshotted the prior image
// to a new archive version we want to surface). The watch key uses
// card_id || isBack-marker so back→back transitions still re-load.
watch(
  () => `${props.card?.card_id ?? ''}|${props.card?.is_back ? 'back' : ''}`,
  loadVersions,
  { immediate: true },
)

// ── Card Meaning (the symbolic read that drives the reveal) ──────
const meaning = ref(null)
const loadingMeaning = ref(false)
const meaningError = ref('')

async function loadMeaning() {
  meaning.value = null
  meaningError.value = ''
  // Card backs have no meaning; only real cards do.
  if (props.card?.is_back || !props.card?.card_id) return
  loadingMeaning.value = true
  try {
    meaning.value = await api.getReaderCardMeaning(props.card.card_id)
  } catch (e) {
    meaningError.value = e?.response?.data?.error || String(e)
  } finally {
    loadingMeaning.value = false
  }
}
watch(() => props.card?.card_id, loadMeaning, { immediate: true })
watch(
  () => props.refreshing,
  (now, prev) => {
    if (prev && !now) loadVersions()
  },
)

async function onMakeLive(versionNumber) {
  if (!props.deckSlug) return
  const isBack = !!props.card?.is_back
  if (!isBack && !props.card?.card_id) return
  makingLive.value = versionNumber
  versionsError.value = ''
  try {
    const r = isBack
      ? await api.makeCustomDeckBackLive(props.deckSlug, versionNumber)
      : await api.makeCustomDeckCardLive(
          props.deckSlug, props.card.card_id, versionNumber,
        )
    // Tell the parent so it can update sampleCards (or draft.back_image_url)
    // and the lightbox's displayed image immediately without waiting for
    // the next poll.
    emit('made-live', {
      cardId: props.card.card_id,
      isBack,
      imageUrl: r.image_url,
    })
    await loadVersions()
  } catch (e) {
    versionsError.value = e?.response?.data?.error || String(e)
  } finally {
    makingLive.value = null
  }
}

function onRefresh() {
  emit('refresh', {
    cardId: props.card?.card_id,
    promptOverride: localPrompt.value,
  })
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '' }
}

function onKey(e) {
  if (e.key === 'Escape') emit('close')
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  stopElapsed()
})
</script>

<style scoped>
.lightbox-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}
.close-btn {
  position: absolute;
  top: 18px;
  right: 24px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(195, 169, 255, 0.3);
  border-radius: 50%;
  color: #f3e9d2;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  z-index: 1;
}
.close-btn:hover { background: rgba(255, 255, 255, 0.12); }

.lightbox-content {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 28px;
  max-width: 1280px;
  width: 100%;
  align-self: center;
}
@media (max-width: 900px) {
  .lightbox-content { grid-template-columns: 1fr; }
}

.image-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.image-frame {
  position: relative;
  display: inline-block;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
}
.big-img {
  max-height: 76vh;
  max-width: 100%;
  height: auto;
  width: auto;
  display: block;
}
.refresh-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.overlay-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(195, 169, 255, 0.25);
  border-top-color: #C9A94E;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
.overlay-text {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 13px;
  letter-spacing: 0.08em;
}
.overlay-elapsed {
  color: rgba(212, 196, 232, 0.7);
  font-size: 12px;
  letter-spacing: 0.04em;
  margin-top: 4px;
}
.overlay-extra {
  color: rgba(212, 196, 232, 0.55);
  font-size: 11px;
  font-style: italic;
  margin-top: 2px;
  text-align: center;
  max-width: 240px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.caption {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 18px;
  letter-spacing: 0.06em;
}
.missing {
  color: rgba(212, 196, 232, 0.4);
  font-style: italic;
  padding: 60px;
}

.edit-pane {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: rgba(20, 14, 36, 0.94);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 12px;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
}
.field-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label > span {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #C9A94E;
}
.field-label > small {
  color: rgba(212, 196, 232, 0.6);
  font-size: 11px;
  font-style: italic;
}
textarea {
  background: rgba(0, 0, 0, 0.42);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
  color: #f3e9d2;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  resize: vertical;
  min-height: 120px;
}
textarea:focus { border-color: #C9A94E; }
textarea:disabled { opacity: 0.55; cursor: not-allowed; }

.locked-note {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.55);
  font-style: italic;
  padding: 8px 12px;
  background: rgba(195, 169, 255, 0.06);
  border-radius: 6px;
}

.actions { display: flex; gap: 10px; }
.primary-btn {
  padding: 10px 22px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  background: #663399;
  color: #fff;
  border: none;
  transition: all 0.18s;
}
.primary-btn:hover:not(:disabled) { background: #7a3fb1; }
.primary-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 0;
}

/* ── Archive ─────────────────────────────────────────────── */
.archive-section {
  margin-top: 6px;
  padding-top: 16px;
  border-top: 1px solid rgba(195, 169, 255, 0.14);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.archive-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.archive-label {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #C9A94E;
}
.archive-count {
  color: rgba(212, 196, 232, 0.55);
  font-size: 11px;
}
.archive-status, .archive-empty {
  color: rgba(212, 196, 232, 0.55);
  font-size: 12px;
  font-style: italic;
}
.archive-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
}
.archive-tile {
  flex: 0 0 auto;
  width: 110px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.archive-frame {
  width: 100%;
  aspect-ratio: 2 / 3;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 6px;
  overflow: hidden;
}
.archive-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.archive-meta {
  text-align: center;
  line-height: 1.3;
}
.archive-version {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  color: rgba(212, 196, 232, 0.85);
}
.archive-date {
  font-size: 10px;
  color: rgba(212, 196, 232, 0.5);
}
.make-live-btn {
  font-size: 10px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 999px;
  color: #C9A94E;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.04em;
}
.make-live-btn:hover:not(:disabled) {
  background: rgba(201, 169, 78, 0.12);
}
.make-live-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Card Meaning section ── */
.meaning-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.meaning-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.meaning-label {
  font-family: 'Cinzel', serif;
  font-size: 14px;
  letter-spacing: 0.06em;
  color: #C9A94E;
}
.meaning-note { font-size: 11px; color: rgba(195, 169, 255, 0.5); font-style: italic; }
.meaning-block {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.meaning-generic { background: rgba(255, 255, 255, 0.015); }
.meaning-source-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(212, 196, 232, 0.7);
  margin-bottom: 6px;
}
.meaning-source-label small { text-transform: none; letter-spacing: 0; opacity: 0.6; }
.meaning-name { margin: 0 0 6px; font-weight: 600; color: #E8D8FF; font-size: 13px; }
.meaning-text { margin: 0 0 6px; font-size: 12.5px; line-height: 1.5; color: rgba(232, 216, 255, 0.9); }
.meaning-text:last-child { margin-bottom: 0; }
.meaning-reversed { color: rgba(232, 216, 255, 0.72); }
.meaning-dim { color: rgba(212, 196, 232, 0.6); }
.meaning-empty { margin: 0; font-size: 12px; font-style: italic; color: rgba(195, 169, 255, 0.55); }
.meaning-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
.meaning-chip {
  font-size: 10.5px;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(201, 169, 78, 0.1);
  border: 1px solid rgba(201, 169, 78, 0.28);
  color: rgba(201, 169, 78, 0.95);
  cursor: help;
}
</style>
